import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Factory, ArrowRight, Zap, LayoutGrid, ShieldCheck, Mail, Lock, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

function fmtErr(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgot, setForgot] = useState(false);

  useEffect(() => { if (!loading && user) navigate("/"); }, [user, loading, navigate]);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(url, body);
      if (data.session_token) localStorage.setItem("session_token", data.session_token);
      setUser(data.user);
      navigate("/");
    } catch (e2) {
      setErr(fmtErr(e2?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1741176505800-caaa3a52631a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent" />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center"><Factory className="w-6 h-6 text-primary-foreground" /></div>
          <div>
            <div className="font-display font-bold text-2xl tracking-wide">TEXFLOW</div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Manufacturing Execution System</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight">Run your<br />textile floor<br /><span className="text-primary">in real time</span></h1>
          <p className="mt-5 text-slate-300 max-w-md">Orders, sampling, live production entry, QC and dispatch — synchronized across every device on the floor, instantly.</p>
          <div className="mt-8 flex gap-6 text-sm">
            <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-primary" /> Live sync</div>
            <div className="flex items-center gap-2"><LayoutGrid className="w-4 h-4 text-primary" /> Full ERP/MES</div>
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Role based</div>
          </div>
        </div>
        <div className="relative text-xs text-slate-500">© 2026 TexFlow. Built for the shop floor.</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center"><Factory className="w-6 h-6 text-primary-foreground" /></div>
            <div className="font-display font-bold text-2xl tracking-wide">TEXFLOW</div>
          </div>

          <h2 className="font-display text-3xl font-bold uppercase tracking-tight">{mode === "login" ? "Sign in" : "Create account"}</h2>
          <p className="text-muted-foreground mt-2 text-sm">{mode === "login" ? "Sign in with your email and password, or use Google." : "Register with your email — an administrator will approve your access."}</p>

          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs">Full name</Label>
                <div className="relative mt-1"><User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="pl-9" data-testid="auth-name" /></div>
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs">Email</Label>
              <div className="relative mt-1"><Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="pl-9" required data-testid="auth-email" /></div>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs">Password</Label>
              <div className="relative mt-1"><Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "register" ? "Min 6 characters" : "••••••••"} className="pl-9" required data-testid="auth-password" /></div>
            </div>
            {mode === "login" && <button type="button" onClick={() => setForgot(true)} className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</button>}
            {err && <p className="text-sm text-destructive" data-testid="auth-error">{err}</p>}
            <Button type="submit" disabled={busy} className="w-full h-11 text-base font-semibold gap-2" data-testid="auth-submit">
              {busy ? "Please wait…" : (mode === "login" ? "Sign in" : "Create account")} <ArrowRight className="w-4 h-4" />
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-3 text-center">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} className="text-primary font-semibold hover:underline" data-testid="auth-toggle">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>

          <div className="flex items-center gap-3 my-5"><div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground uppercase tracking-wider">or</span><div className="h-px bg-border flex-1" /></div>

          <Button onClick={handleGoogle} variant="outline" data-testid="google-login-btn" className="w-full h-11 text-base font-semibold gap-2">
            Continue with Google <ArrowRight className="w-4 h-4" />
          </Button>

          <p className="text-xs text-muted-foreground mt-6 leading-relaxed">New accounts have no access until an administrator approves them. After approval, you'll be assigned a role (Manager, Supervisor, Operator, QC, Packing or read-only Viewer).</p>
        </div>
      </div>

      {forgot && <ForgotDialog onClose={() => setForgot(false)} />}
    </div>
  );
}

function ForgotDialog({ onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      toast.success(data?.message || "If an account exists, a reset link was generated.");
    } catch { toast.error("Could not process request"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="forgot-dialog">
        <DialogHeader>
          <DialogTitle>Reset your password</DialogTitle>
          <DialogDescription>Enter your email and we'll generate a reset link. If you don't receive it, please ask your administrator to reset your password.</DialogDescription>
        </DialogHeader>
        {sent ? (
          <p className="text-sm text-muted-foreground" data-testid="forgot-sent">If an account exists for <span className="font-medium">{email}</span>, a reset link has been generated. Please check with your administrator if needed.</p>
        ) : (
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="forgot-email" />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="forgot-close">Close</Button>
          {!sent && <Button onClick={submit} disabled={busy || !email.trim()} data-testid="forgot-submit">Send reset link</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
